import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React from 'react';

import Box from '../box/box.jsx';
import Dots from './dots.jsx';
import helpIcon from './icons/help.svg';
import backIcon from './icons/back.svg';
import usbAndBluetoothIcon from './icons/usb-and-bluetooth.svg';

import styles from './connection-modal.css';

/**
 * Tela exibida quando há pelo menos 1 porta USB (Web Serial) disponível:
 * "Dispositivo conectado" em vez das instruções do OpenBlock Link.
 */
const UsbConnectedStep = ({onCancel}) => (
    <Box className={styles.body}>
        <Box className={classNames(styles.activityArea, styles.usbConnectedArea)}>
            <div className={styles.usbConnectedMessage}>
                <FormattedMessage
                    defaultMessage="Device connected (USB)"
                    description="Title when USB port is available"
                    id="gui.connection.unavailable.usbConnectedTitle"
                />
                <p className={styles.usbConnectedDetail}>
                    <FormattedMessage
                        defaultMessage="Your board is available via Web Serial. Use the Upload button to flash your program to the board."
                        description="Instruction to use Upload button"
                        id="gui.connection.unavailable.usbConnectedDetail"
                    />
                </p>
            </div>
        </Box>
        <Box className={styles.bottomArea}>
            <Dots
                className={styles.bottomAreaItem}
                success
                total={3}
            />
            <button
                className={classNames(styles.bottomAreaItem, styles.connectionButton)}
                onClick={onCancel}
            >
                <FormattedMessage
                    defaultMessage="Close"
                    description="Button to close the modal"
                    id="gui.connection.unavailable.closeButton"
                />
            </button>
        </Box>
    </Box>
);

UsbConnectedStep.propTypes = {
    onCancel: PropTypes.func.isRequired
};

/**
 * Tela quando não há porta USB disponível: apenas a instrução de conectar cabo USB / Bluetooth,
 * no mesmo estilo da tela "dispositivo conectado" (sem menção ao OpenBlock Link).
 */
const CheckConnectionStep = ({onCancel, onScanning, onHelp}) => (
    <Box className={styles.body}>
        <Box className={classNames(styles.activityArea, styles.usbConnectedArea)}>
            <div className={styles.usbConnectedMessage}>
                <img
                    className={styles.checkConnectionIcon}
                    src={usbAndBluetoothIcon}
                    alt=""
                />
                <p className={styles.usbConnectedDetail}>
                    <FormattedMessage
                        defaultMessage="Check that USB cable is connected or Bluetooth is enabled"
                        description="Message for making sure USB cable is connected or Bluetooth is enabled"
                        id="gui.connection.unavailable.connectUsbEnableBluetooth"
                    />
                </p>
            </div>
        </Box>
        <Box className={styles.bottomArea}>
            <Dots
                className={styles.bottomAreaItem}
                total={3}
            />
            <Box className={classNames(styles.bottomAreaItem, styles.buttonRow)}>
                <button
                    className={styles.connectionButton}
                    onClick={onScanning}
                >
                    <img
                        className={classNames(styles.buttonIconLeft, styles.buttonIconBack)}
                        src={backIcon}
                    />
                    <FormattedMessage
                        defaultMessage="Try again"
                        description="Button to initiate trying the device connection again after an error"
                        id="gui.connection.unavailable.tryagainbutton"
                    />
                </button>
                <button
                    className={styles.connectionButton}
                    onClick={onHelp}
                >
                    <img
                        className={styles.buttonIconLeft}
                        src={helpIcon}
                    />
                    <FormattedMessage
                        defaultMessage="Help"
                        description="Button to view help content"
                        id="gui.connection.unavailable.helpbutton"
                    />
                </button>
            </Box>
        </Box>
    </Box>
);

CheckConnectionStep.propTypes = {
    onCancel: PropTypes.func,
    onScanning: PropTypes.func.isRequired,
    onHelp: PropTypes.func
};

const UnavailableStep = props => {
    const isSerialport = props.isSerialport;
    const usbPortCount = props.usbPortCount;
    const showUsbConnected = isSerialport && typeof usbPortCount === 'number' && usbPortCount >= 1;

    if (showUsbConnected) {
        return <UsbConnectedStep onCancel={props.onCancel} />;
    }

    return (
        <CheckConnectionStep
            onCancel={props.onCancel}
            onScanning={props.onScanning}
            onHelp={props.onHelp}
        />
    );
};

UnavailableStep.propTypes = {
    isSerialport: PropTypes.bool,
    usbPortCount: PropTypes.number,
    onCancel: PropTypes.func,
    onHelp: PropTypes.func,
    onScanning: PropTypes.func
};

export default UnavailableStep;
